/**
 * Display-only filters for the Opportunities tables (MCX and NFO).
 *
 * The backend already returns the eligible rows ranked by %Profit (N) descending. These
 * filters only HIDE rows; they never re-sort, re-rank or recompute anything, so the order
 * that comes back is exactly the backend's order minus the hidden rows:
 *
 *   universe -> backend eligibility/formulas -> Profit % Min -> OI Min (both legs) -> backend order
 */

/** A minimum the user typed, as a number — or null when the filter is off (blank, 0, invalid). */
export function parseMin(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** %Profit (N) in percent. The API carries it as a fraction (0.03 = 3%). */
/** Return on margin in percent for the Profit % Min filter: the spread scenario's net convergence P&L / Zerodha
 *  margin (27 Oct 2026). A row whose margin is not known yet cannot prove it meets the minimum (hidden while set). */
export const profitPercent = (row) => {
  const v = row?.scenario?.return_on_margin_pct;
  return v === null || v === undefined ? NaN : Number(v);
};

/** True when the row passes both filters. A missing OI can never prove it is >= the minimum,
 *  so with OI Min set such a row is hidden rather than assumed to pass. */
export function passesFilters(row, { profitPctMin, oiMin } = {}) {
  const p = parseMin(profitPctMin);
  if (p !== null && !(profitPercent(row) >= p)) return false;
  const o = parseMin(oiMin);
  if (o !== null) {
    const near = row?.near_oi, far = row?.far_oi;
    if (near === null || near === undefined || far === null || far === undefined) return false;
    if (!(Number(near) >= o && Number(far) >= o)) return false;
  }
  return true;
}

/** Filter, preserving the incoming (backend %Profit-descending) order exactly. */
export function filterOpportunities(rows, filters) {
  return (rows || []).filter((r) => passesFilters(r, filters));
}

export const FILTER_STORAGE_KEY = "gz.opportunities.filters.v1";
// OI Min (both legs) starts ON: a leg with almost no open interest may never fill (the audit saw LEAD / NICKEL
// rows with a far-leg OI of 0 and 4 carrying an active Add). A viewer who clears it keeps it cleared.
export const DEFAULT_OI_MIN = "100";

/** Per-viewer convenience only; any storage failure falls back to "no filter". */
export function loadSavedFilters() {
  try {
    const raw = window.localStorage.getItem(FILTER_STORAGE_KEY);
    const v = raw ? JSON.parse(raw) : {};
    return { profitPctMin: v.profitPctMin ?? "", oiMin: v.oiMin ?? DEFAULT_OI_MIN };
  } catch {
    return { profitPctMin: "", oiMin: DEFAULT_OI_MIN };
  }
}

export function saveFilters(filters) {
  try { window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters)); } catch { /* ignore */ }
}
